/**
 * What this app costs Firestore, counted by the app itself.
 *
 * Firestore tells a client nothing about its own billing. There is no read count to ask
 * for, and the Cloud Monitoring API that has one needs a server — which this project, on
 * the free plan with no Cloud Functions, does not have. The Firebase console has the real
 * numbers and always will.
 *
 * So this counts what the app does: documents delivered to its listeners, writes it issues,
 * deletes it issues. Two honest caveats, both worth stating wherever the figures are shown:
 * it is this app's usage rather than the project's — it cannot see the console, a script, or
 * an import somebody runs — and a document served from the on-device cache is not billed but
 * is counted, so the figures read high rather than low. High is the right direction for a
 * number you are watching for trouble.
 */

export type Usage = { reads: number; writes: number; deletes: number }

/**
 * Reads broken down by the collection they came from.
 *
 * Kept because a day that ends over the free allowance otherwise says only that it did.
 * Nobody can act on 50,000; "the field guide, 31,000" names the thing to change. The keys
 * are collection names — for a subcollection read across every parent, the subcollection's
 * own name — and a read the app could not attribute is counted under `unknown` rather than
 * dropped, so the parts still add up to the whole.
 */
export type ReadSources = Record<string, number>

/**
 * Writes broken down by the collection they went to.
 *
 * Writes are nowhere near their allowance, so this is not about the bill. It is that four
 * hundred writes on a day nobody edited anything is a question, and "which collection" is
 * the first half of the answer — the same reason the reads carry a breakdown.
 *
 * Deletes are billed on their own counter and are left out, so the parts of this still add
 * up to the writes figure beside it.
 */
export type WriteSources = Record<string, number>

export type UsageDay = Usage & { day: string; sources?: ReadSources; writeSources?: WriteSources }

/**
 * Reads broken down by the tab that made them.
 *
 * A day total says a day went over; it does not say whether that was six people working or
 * one tab looping while nobody was at a keyboard. The keys are `person~pageload`, so the
 * two cases look different at a glance: six people are six keys, a loop is one key with
 * everything under it, and a tab reloading twenty times is twenty keys.
 */
export type Clients = Record<string, number>

/** One clock hour of a day. Recorded alongside the day, never instead of it. */
export type UsageHour = Usage & {
  hour: string
  sources?: ReadSources
  writeSources?: WriteSources
  clients?: Clients
  clientWrites?: Clients
}

/** Firestore's free daily allowance, which is what this project runs on. */
export const FREE_TIER = { reads: 50_000, writes: 20_000, deletes: 20_000 } as const

export const EMPTY: Usage = { reads: 0, writes: 0, deletes: 0 }

/** The calendar day a tally belongs to, in the reader's own timezone. */
export const dayKey = (at: Date): string =>
  `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`

/**
 * The clock hour a tally belongs to, on the same clock as `dayKey`.
 *
 * Two digits so that ordering by name is ordering by hour, which is what the document id
 * is for: the hours of a day arrive from Firestore already in the order they are read in.
 */
export const hourKey = (at: Date): string => String(at.getHours()).padStart(2, '0')

/**
 * The tab a read came from: who was signed in, and which page load.
 *
 * The domain is dropped because everyone who can sign in shares one of two, and what is
 * left is reduced to letters, digits and dashes — this becomes a map key, and a key with a
 * slash in it reads as a path. A page load is counted apart from the one before it on
 * purpose: twenty reloads cost twenty cold reads and should look like twenty things.
 */
export const clientKey = (email: string | null | undefined, session: string): string => {
  const local = (email ?? '').split('@')[0] ?? ''
  const safe = local.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '')
  return `${safe || 'anon'}~${session}`
}

const valid = (n: number) => Number.isFinite(n) && n > 0

/**
 * A running tally held in memory and drained periodically.
 *
 * Kept out of Firestore until it is drained because writing on every snapshot would cost
 * more writes than the reads it is measuring.
 */
export function makeMeter() {
  let pending: Usage = { ...EMPTY }
  let sources: ReadSources = {}
  let writeSources: WriteSources = {}
  return {
    reads: (n: number, source = 'unknown') => {
      if (!valid(n)) return
      pending.reads += n
      sources[source] = (sources[source] ?? 0) + n
    },
    writes: (n: number, source = 'unknown') => {
      if (!valid(n)) return
      pending.writes += n
      writeSources[source] = (writeSources[source] ?? 0) + n
    },
    // A delete carries its collection for the caller's convenience and nothing records it:
    // deletes are a handful a day and keeping them out of the write breakdown is what makes
    // that breakdown add up to the writes figure beside it.
    deletes: (n: number, _source = 'unknown') => { if (valid(n)) pending.deletes += n },
    peek: (): Tally => ({ ...pending, sources: { ...sources }, writeSources: { ...writeSources } }),
    /** Hands over what has accrued and starts again, so nothing is counted twice. */
    drain: (): Tally => {
      const out = { ...pending, sources, writeSources }
      pending = { ...EMPTY }
      sources = {}
      writeSources = {}
      return out
    },
  }
}

/** What one flush hands over: the three counters and the two breakdowns. */
export type Tally = Usage & { sources: ReadSources; writeSources: WriteSources }

/**
 * A Firestore path reduced to the thing worth naming.
 *
 * `decisions/D2-001/comments` is reported as `comments`, not as one key per decision: a
 * breakdown with a thousand rows in it is the same as no breakdown. The last segment is
 * always the collection, because a path that ends in a document has its parent passed in.
 */
export const readSourceFromPath = (path: string): string =>
  path.split('/').filter(Boolean).at(-1) ?? 'unknown'

const ranked = (counts: Record<string, number> | undefined): Array<[string, number]> =>
  Object.entries(counts ?? {}).sort((a, b) => b[1] - a[1])

/** The biggest sources first — the only order in which the list answers anything. */
export const rankSources = (sources: ReadSources): Array<{ source: string; reads: number }> =>
  ranked(sources).map(([source, reads]) => ({ source, reads }))

/** The busiest tab first, for the same reason: it is the one that has to be explained. */
export const rankClients = (clients: Clients | undefined): Array<{ client: string; reads: number }> =>
  ranked(clients).map(([client, reads]) => ({ client, reads }))

/**
 * Every collection's reads added up across a run of days.
 *
 * "What did today spend its reads on" and "what does this app spend its reads on" are
 * different questions, and the second one should not be decided by a single day of imports.
 * Days recorded before the breakdown existed carry none and contribute nothing.
 */
export const mergeSources = (days: UsageDay[]): ReadSources => {
  const total: ReadSources = {}
  for (const day of days) {
    for (const [source, reads] of Object.entries(day.sources ?? {})) {
      total[source] = (total[source] ?? 0) + reads
    }
  }
  return total
}

/**
 * A day as twenty-four hours, whether or not anything was recorded in them.
 *
 * A chart of only the hours that have a tally hides what it exists to show — that the other
 * eighteen were quiet. The empty hours are the shape of the answer.
 */
export const fillHours = (hours: UsageHour[]): UsageHour[] => {
  const byHour = new Map(hours.map((h) => [h.hour, h]))
  return Array.from({ length: 24 }, (_, i) => {
    const hour = String(i).padStart(2, '0')
    return byHour.get(hour) ?? { hour, ...EMPTY }
  })
}

/** How much of a day's free allowance each kind used. Not capped: going over is the point. */
export const share = (usage: Usage) => ({
  reads: usage.reads / FREE_TIER.reads,
  writes: usage.writes / FREE_TIER.writes,
  deletes: usage.deletes / FREE_TIER.deletes,
})

export function summarise(days: UsageDay[]): {
  total: Usage
  perDay: Usage
  busiest: UsageDay | null
} {
  const total = days.reduce<Usage>((sum, d) => ({
    reads: sum.reads + d.reads,
    writes: sum.writes + d.writes,
    deletes: sum.deletes + d.deletes,
  }), { ...EMPTY })

  const n = days.length || 1
  return {
    total,
    perDay: {
      reads: Math.round(total.reads / n),
      writes: Math.round(total.writes / n),
      deletes: Math.round(total.deletes / n),
    },
    // Reads are what runs out first on this plan by an order of magnitude, so they decide
    // which day was the busiest.
    busiest: days.length === 0
      ? null
      : days.reduce((worst, d) => (d.reads > worst.reads ? d : worst), days[0]!),
  }
}
